import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconAttachment } from '@components/icons/attachment';
import { IconSelectMultiple } from '@components/icons/select-multiple';
import { IconSelectSingle } from '@components/icons/select-single';
import { IconTextLong } from '@components/icons/text-long';
import { IconTextShort } from '@components/icons/text-short';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { CloseButton } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { CareerSiteLanguageType } from '@locale/locale-store';
import { useScopedTranslate } from '@locale/translate';

import { JobQuestionMultiLanguage } from 'types/jobs';

import { useSelectSidebar } from './select-sidebar';
import { useTextFileSidebar } from './text-file-sidebar';

export const useQuestionsidebar = () => {
  const sidebar = useSidebar(QuestionSidebar, {
    variant: 'LARGE',
  });
  return sidebar;
};

type QuestionType = {
  title: string;
  description: string;
  icon: React.ReactNode;
  openSidebar?: () => void;
  dataCy: string;
};

export type QuestionSidebarProps = {
  languages: CareerSiteLanguageType[];
  addQuestion?: (question: JobQuestionMultiLanguage) => void;
};

const QuestionSidebar = (props: QuestionSidebarProps) => {
  const { languages, addQuestion } = props;
  const { t } = useScopedTranslate(locale);
  const showTextFileSidebar = useTextFileSidebar();
  const showSelectSidebar = useSelectSidebar();

  const questionTypes: QuestionType[] = [
    {
      title: t('shortAnswer'),
      description: t('shortAnswerDescription'),
      icon: <IconTextShort />,
      dataCy: 'short-answer',
      openSidebar: () =>
        showTextFileSidebar({
          variant: 'one_line',
          languages: languages,
          addQuestion,
        }),
    },
    {
      title: t('paragraph'),
      description: t('paragraphDescription'),
      icon: <IconTextLong />,
      dataCy: 'paragraph',
      openSidebar: () =>
        showTextFileSidebar({
          variant: 'multi_line',
          languages: languages,
          addQuestion,
        }),
    },
    {
      title: t('singleSelect'),
      description: t('singleSelectDescription'),
      icon: <IconSelectSingle />,
      dataCy: 'single-select',
      openSidebar: () =>
        showSelectSidebar({
          variant: 'dropdown',
          languages: languages,
          addQuestion,
        }),
    },
    {
      title: t('multiSelect'),
      description: t('multiSelectDescription'),
      icon: <IconSelectMultiple />,
      dataCy: 'multiselect',
      openSidebar: () =>
        showSelectSidebar({
          variant: 'multidropdown',
          languages: languages,
          addQuestion,
        }),
    },
    {
      title: t('attachment'),
      description: t('attachmentDescription'),
      icon: <IconAttachment />,
      dataCy: 'attachment',
      openSidebar: () =>
        showTextFileSidebar({
          variant: 'attachment',
          languages: languages ?? ([] as CareerSiteLanguageType[]),
          addQuestion,
        }),
    },
  ];
  return (
    <Sidebar>
      <Sidebar.Header>
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack space={2} orientation="horizontal" align="center">
            <IconSelectSingle color="purple50" />
            <Sidebar.HeaderTitle title={t('questionTypeTitle')} />
          </Stack>
          <CloseButton />
        </Stack>
      </Sidebar.Header>
      <Sidebar.Content>
        <Stack space={6}>
          <Stack space={2}>
            {questionTypes.map((question, index) => (
              <Card
                key={question.title}
                variant="transparent"
                spaceX={2}
                spaceY={4}
                interactive
                onClick={question.openSidebar}
                style={{ cursor: 'pointer' }}
                dataCy={question.dataCy}
              >
                <Stack space={4} key={question.title} orientation="horizontal" align="center">
                  {question.icon}
                  <div>
                    <Text text={question.title} font="bold14" />
                    <Text text={question.description} font="regular14" />
                  </div>
                </Stack>
              </Card>
            ))}
          </Stack>
          <Stack align="end">
            <Button variant="secondary" onClick={hideSidebar} text={t('cancel')} />
          </Stack>
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

const locale = {
  en: {
    questionTypeTitle: 'Select question type',
    shortAnswer: 'Text - Short answer',
    shortAnswerDescription: 'For simple questions',
    paragraph: 'Text - Long answer',
    paragraphDescription: 'When you want more detailed answers',
    singleSelect: 'Select - Single select',
    singleSelectDescription: 'Where only one option is applicable',
    multiSelect: 'Select - Multi select',
    multiSelectDescription: 'Where more than one answer is allowed',
    attachment: 'Attachment - File upload',
    attachmentDescription: 'For file attachments',
    special: 'Special',
    back: 'Back',
    cancel: 'Cancel',
  },
  is: {
    questionTypeTitle: 'Veldu tegund spurningar',
    shortAnswer: 'Texti - Stutt svar',
    shortAnswerDescription: 'Fyrir einfaldar spurningar',
    paragraph: 'Texti - Langt svar',
    paragraphDescription: 'Þegar þú vilt fá meiri upplýsingar',
    singleSelect: 'Fellilisti - Eitt val',
    singleSelectDescription: 'Þar sem að aðeins einn valmöguleiki er valinn',
    multiSelect: 'Fellilisti - Fjölval',
    multiSelectDescription: 'Þar sem að margir valkostir eru gildir',
    attachment: 'Viðhengi - Hlaða upp skrá',
    attachmentDescription: 'Fyrir öll skráarviðhengi',
    special: 'Sérstakt',
    back: 'Til baka',
    cancel: 'Hætta við',
  },
};
